/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// state
import { TStore } from '@store/index';
import { StateCreator } from 'zustand';

// datatable additionalities
import { tableSchemaBase as columns } from '../additionalities.columns';

// types
import {
	TQueryParams,
	TSortStatus,
} from '@global-types/index';
import { TMeasurabilityProject } from '@pages/Offsets/interfaces';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */
export interface IAdditionalitiesSlice {
	/* Pagination */
	additionalitiesTotalPages: number;
	additionalitiesPage: number;
	additionalitiesTotal: number;
	additionalitiesPageSize: number;

	additionalitiesQueryParams: TQueryParams;
	additionalitiesSelectedRow: Partial<TMeasurabilityProject>;
	additionalitiesSortParams: string;
	additionalitiesSortStatus: {};
	additionalitiesOpenConfirmationModalDelete: {
		open: boolean;
		params: string;
	};
	uploadedTechnicalFiles: any[];

	/* Setters */
	setAdditionalitiesPage: (_page: number) => void;
	setUploadedTechnicalFiles: (
		_technicalFiles: any
	) => void;
	setAdditionalitiesPageSize: (_pageSize: number) => void;
	setAdditionalitiesTotalPages: (
		_totalPages: number
	) => void;

	setAdditionalitiesQueryParams: (
		_additionalitiesQueryParams: TQueryParams
	) => void;
	setAdditionalitiesSelectedRow: (
		_additionalitiesSelectedRow: Partial<TMeasurabilityProject>
	) => void;
	setAdditionalitiesSortParams: (
		_additionalitiesSortParams: string
	) => void;

	setAdditionalitiesSortStatus: (
		_additionalitiesSortStatus: TSortStatus
	) => void;

	setAdditionalitiesOpenConfirmationModalDelete: (_additionalitiesOpenConfirmationModalDelete: {
		open: boolean;
		params: any;
	}) => void;
}

/* -------------------------------------------------------------------------- */
/*                                    Slice                                   */
/* -------------------------------------------------------------------------- */

export const partializeAdditionalitiesSlice = (
	s: any
): any => ({
	additionalitiesOptions: s.additionalitiesOptions,
});

const initialState = {
	additionalitiesOptions: columns.filter(
		(item) => item.defaultVisible
	),
	additionalitiesTotalPages: 10,
	additionalitiesPage: 1,
	additionalitiesTotal: 0,
	additionalitiesPageSize: 10,
	uploadedTechnicalFiles: [],

	additionalitiesSelectedRecords: [],
	additionalitiesQueryParams: {},
	additionalitiesSelectedRow: {},
	additionalitiesSortParams: '-createdAt',
	additionalitiesSortStatus: {},
	additionalitiesOpenConfirmationModalDelete: {
		open: false,
		params: '',
	},
};

export const createAdditionalitiesSlice: StateCreator<
	TStore,
	[['zustand/persist', any]],
	[],
	IAdditionalitiesSlice
> = (set) => ({
	...initialState,
	setUploadedTechnicalFiles: (_page: any) =>
		set({ uploadedTechnicalFiles: _page }),
	setAdditionalitiesPage: (_page: number) =>
		set({ additionalitiesPage: _page }),
	setAdditionalitiesPageSize: (_pageSize: number) =>
		set({ additionalitiesPageSize: _pageSize }),
	setAdditionalitiesTotalPages: (_totalPages: number) =>
		set({ additionalitiesTotalPages: _totalPages }),

	setAdditionalitiesQueryParams: (
		_additionalitiesQueryParams: TQueryParams
	) =>
		set({
			additionalitiesQueryParams:
				_additionalitiesQueryParams,
		}),
	setAdditionalitiesSelectedRow: (
		_additionalitiesSelectedRow: Partial<TMeasurabilityProject>
	) =>
		set({
			additionalitiesSelectedRow:
				_additionalitiesSelectedRow,
		}),
	setAdditionalitiesSortParams: (
		_additionalitiesSortParams: string
	) =>
		set({
			additionalitiesSortParams:
				_additionalitiesSortParams,
		}),

	setAdditionalitiesSortStatus: (
		_sortStatus: TSortStatus
	) => set({ additionalitiesSortStatus: _sortStatus }),

	setAdditionalitiesOpenConfirmationModalDelete:
		(_additionalitiesOpenConfirmationModalDelete: {
			open: boolean;
			params: any;
		}) =>
			set({
				additionalitiesOpenConfirmationModalDelete:
					_additionalitiesOpenConfirmationModalDelete,
			}),
});
